import { CampoValor, Parte, Email } from "./types";
import { formatCurrency } from "../../../../../../../utility/currencyUtils";
import { parseJsonSafely } from "../../../../../../../utility/jsonUtils";

const formatDate = (value: string | Date | null | undefined, includeTimezone?: string): string => {
    if (!value) return '—';
    try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return '—';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const dateStr = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        return includeTimezone ? `${dateStr} ${includeTimezone}` : dateStr;
    } catch {
        return '—';
    }
};

const applyCNJMask = (value: string | undefined | null): string => {
    if (!value) return '—';
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 7) return numbers;
    if (numbers.length <= 9) return `${numbers.slice(0, 7)}-${numbers.slice(7)}`;
    if (numbers.length <= 13) return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9)}`;
    if (numbers.length <= 14) return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9, 13)}.${numbers.slice(13)}`;
    if (numbers.length <= 16) return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9, 13)}.${numbers.slice(13, 14)}.${numbers.slice(14)}`;
    return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9, 13)}.${numbers.slice(13, 14)}.${numbers.slice(14, 16)}.${numbers.slice(16, 20)}`;
};

const formatCEP = (value: string | undefined | null): string => {
    if (!value) return '—';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 8) {
        return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
    }
    return value;
};

const formatValue = (key: string, value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    
    const keyLower = key.toLowerCase();
    
    if (keyLower.includes('valor')) {
        return formatCurrency(value);
    }
    
    if (keyLower.includes('data') || keyLower.includes('prazo') || keyLower.includes('criado') || keyLower.includes('modificado')) {
        return formatDate(value);
    }
    
    if (keyLower.includes('processo')) {
        return applyCNJMask(value);
    }
    
    if (keyLower === 'cep') {
        return formatCEP(value);
    }
    
    if (typeof value === 'boolean') {
        return value ? 'Sim' : 'Não';
    }
    
    return String(value);
};

const getFieldLabel = (key: string): string => {
    const labelMap: Record<string, string> = {
        'id_comarca': 'Comarca',
        'id_servico': 'Serviço',
        'id_usuario': 'Profissional',
        'id_operador': 'Operador',
        'numero_processo': 'Número do Processo',
        'nome_cliente': 'Nome do Cliente',
        'codigo_cliente': 'Código do Cliente',
        'valor_cliente': 'Valor do Cliente',
        'valor_profissional': 'Valor do Serviço',
        'valor_adiantado': 'Valor Adiantado',
        'prazo_aceite': 'Prazo para Aceite',
        'prazo_para_conclusao': 'Prazo para Conclusão',
        'dias_uteis_para_conclusao': 'Dias Úteis para Conclusão',
        'data_conclusao': 'Data de Conclusão',
        'data_inicio': 'Data de Início',
        'data': 'Data',
        'data_tz': 'Fuso Horário',
        'orientacao': 'Orientações Prévias',
        'orientacoes': 'Orientações Prévias',
        'status': 'Status',
        'tipo_audiencia': 'Tipo',
        'profissional_necessario': 'Profissional Necessário',
        'modalidade': 'Modalidade',
        'endereco_virtual': 'Endereço Virtual',
        'rua': 'Rua',
        'bairro': 'Bairro',
        'cep': 'CEP',
        'numero': 'Número',
        'complemento': 'Complemento',
        'natureza': 'Natureza',
        'lucro': 'Lucro',
        'lucro_porcentagem': 'Lucro (%)',
        'criado': 'Criado em',
        'modificado': 'Modificado em',
    };
    
    return labelMap[key] || key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

export const mapearDadosProposta = (dadosProposta: any): CampoValor[] => {
    if (!dadosProposta || typeof dadosProposta !== 'object') {
        return [];
    }
    
    const ordemCampos = [
        { key: 'uf_comarca', label: 'Estado' },
        { key: 'cidade_comarca', label: 'Comarca' },
        { key: 'natureza', label: 'Natureza' },
        { key: 'numero_processo', label: 'Número do Processo' },
        { key: 'valor_cliente', label: 'Valor do Cliente' },
        { key: 'valor_profissional', label: 'Valor do Serviço' },
        { key: 'data', label: 'Prazo Fatal' },
        { key: 'data_tz', label: 'Fuso Horário' },
        { key: 'nome_cliente', label: 'Nome' },
        { key: 'codigo_cliente', label: 'Código do Cliente' },
        { key: 'id_escritorio_solicitante', label: 'Solicitante' },
        { key: 'tipo_audiencia', label: 'Tipo' },
        { key: 'modalidade', label: 'Modalidade' },
        { key: 'profissional_necessario', label: 'Profissional Necessário' },
        { key: 'cep', label: 'CEP' },
        { key: 'rua', label: 'Rua' },
        { key: 'bairro', label: 'Bairro' },
        { key: 'numero', label: 'Número' },
        { key: 'complemento', label: 'Complemento' },
        { key: 'nome_correspondente', label: 'Correspondente' },
        { key: 'prazo_aceite', label: 'Prazo para aceite' },
        { key: 'prazo_para_conclusao', label: 'Prazo para conclusão' },
        { key: 'orientacoes', label: 'Orientações Prévias' },
        { key: 'advogado_parte_contraria', label: 'Advogado Parte Contrária' },
        { key: 'status', label: 'Status' },
    ];
    
    const campos: CampoValor[] = [];
    
    for (const { key, label } of ordemCampos) {
        let value = dadosProposta[key];
        
        if (key === 'nome_correspondente' && !value) {
            value = dadosProposta.correspondente_nome || dadosProposta.nome_correspondente || dadosProposta.nomeProfissional || dadosProposta.nome_profissional;
        }
        
        if (key === 'advogado_parte_contraria' && !value) {
            value = dadosProposta.advogado_parte_contraria_nome || dadosProposta.advogado_nome;
        }
        
        if (key === 'orientacoes' && !value) {
            value = dadosProposta.orientacao;
        }
        
        if (key === 'data' && !value) {
            value = dadosProposta.prazo_fatal || dadosProposta.data;
        }
        
        if (key === 'prazo_para_conclusao' && !value) {
            if (dadosProposta.dias_uteis_para_conclusao) {
                value = `${dadosProposta.dias_uteis_para_conclusao} dias`;
            } else if (dadosProposta.prazo_para_conclusao) {
                value = dadosProposta.prazo_para_conclusao;
            }
        }
        
        if (key === 'id_escritorio_solicitante' && value) {
            value = `ES${value}`;
        }
        
        let valorFormatado: string;
        
        if (key === 'data_tz') {
            if (value) {
                const tzMap: Record<string, string> = {
                    'BRT': 'BRT (UTC-3) - Brasília',
                    'AMT': 'AMT (UTC-4) - Manaus',
                    'ACT': 'ACT (UTC-5) - Rio Branco',
                    'FNT': 'FNT (UTC-2) - Fernando de Noronha',
                    'BRST': 'BRST (UTC-2) - Brasília',
                    'UTC': 'UTC (UTC+0)',
                };
                valorFormatado = tzMap[value] || `${value} (UTC)`;
            } else {
                valorFormatado = '—';
            }
        } else if (key === 'data') {
            valorFormatado = formatDate(value, dadosProposta.data_tz || 'BRT');
        } else if (key === 'prazo_aceite') {
            valorFormatado = formatDate(value, dadosProposta.data_tz || 'BRT');
        } else if (key === 'prazo_para_conclusao') {
            valorFormatado = formatDate(value, dadosProposta.data_tz || 'BRT');
        } else {
            valorFormatado = formatValue(key, value);
        }
        
        // Não adicionar campos com valor vazio
        if (valorFormatado && valorFormatado !== '—' && valorFormatado.trim() !== '') {
            campos.push({
                campo: label,
                valor: valorFormatado
            });
        }
    }
    
    return campos;
};

export const mapearPartes = (logs: any[]): Parte[] => {
    const partes: Parte[] = [];
    
    const logsPartesProcesso = logs.filter((log: any) => log.entidade_nome === 'partes_processos');
    const logsPartesRepresentadas = logs.filter((log: any) => log.entidade_nome === 'partes_representadas');
    
    for (const log of logsPartesProcesso) {
        const dadosDepois = parseJsonSafely(log.dados_depois);
        
        if (dadosDepois?.nome && dadosDepois?.tipo) {
            const representada = dadosDepois.id_parte_representada != null;
            partes.push({
                tipo: dadosDepois.tipo,
                nome: dadosDepois.nome,
                representada
            });
        }
    }
    
    for (const log of logsPartesRepresentadas) {
        const dadosDepois = parseJsonSafely(log.dados_depois);
        
        if (dadosDepois?.nome) {
            partes.push({
                tipo: 'PARTE REPRESENTADA',
                nome: dadosDepois.nome,
                representada: true
            });
        }
    }
    
    return partes;
};

export { mapearEmail } from "../../../../../../../utility/historicoLogUtils";

